import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { UseFormReturn } from "react-hook-form";
import { PatchPolicyFormValues } from "../../schema";

interface UpdatesTabProps {
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function UpdatesTab({ form }: UpdatesTabProps) {
  return (
    <div className="space-y-8">
      {/* Update Types Section */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Update Types</h3>
        <p className="text-sm text-muted-foreground mb-6">Control which types of updates are managed</p>
        
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="auto_download"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Check for updates</FormLabel>
                  <FormDescription>
                    Enable automatic checking for available updates
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="auto_download"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Download updates automatically</FormLabel>
                  <FormDescription>
                    Automatically download updates when they become available
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="allow_major_updates"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Install macOS updates</FormLabel>
                  <FormDescription>
                    Allow installation of macOS system updates
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="allow_security_updates"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Install security updates</FormLabel>
                  <FormDescription>
                    Automatically install system data files and security updates
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="allow_minor_updates"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Install app updates</FormLabel>
                  <FormDescription>
                    Allow App Store app updates to install automatically
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Deferral Settings Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-2">OS Deferral Settings</h3>
        <p className="text-sm text-muted-foreground mb-6">Delay updates by a specified number of days</p>
        
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="force_install_days"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Defer major OS updates</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input 
                        type="number" 
                        min={0}
                        max={90}
                        className="w-20"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <span className="text-sm text-muted-foreground">days</span>
                  </div>
                </div>
                <FormDescription>
                  Number of days to defer major macOS version updates (0-90)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="force_install_days"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Defer minor OS updates</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input 
                        type="number" 
                        min={0}
                        max={90}
                        className="w-20"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <span className="text-sm text-muted-foreground">days</span>
                  </div>
                </div>
                <FormDescription>
                  Number of days to defer minor macOS updates (0-90)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="force_install_days"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Defer non-OS updates</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input 
                        type="number" 
                        min={0}
                        max={90}
                        className="w-20"
                        {...field}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <span className="text-sm text-muted-foreground">days</span>
                  </div>
                </div>
                <FormDescription>
                  Number of days to defer app and other non-OS updates (0-90)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_os_version"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Maximum macOS Version (Optional)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g., 15.2" />
                </FormControl>
                <FormDescription>
                  Prevent updates beyond this version (leave empty for no limit)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}
